/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/icon.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { browserCheck } from '@ta-interaktiv/browsercheck'
import { MunicipalitySearch } from '@ta-interaktiv/react-municipality-search'
import { SunGrid } from 'components/sungrid'
import { Highlighter } from 'components/highlighter'
import Cover from '../cover'
//import { json } from 'd3-fetch'
// import { format } from 'd3-format'
import * as d3 from 'd3'
import { Link } from '@ta-interaktiv/contentbricks'

const numberFormat = d3.format('.1f')

// TODO Set the correct article ID here
const ARTICLE_ID = '989918606307'
const isMobile = browserCheck.isMobile

export class AppComponent extends React.Component<Props, State> {
  constructor() {
    super()
    this.state = {
      locationNum: -1,
      locationName: '[not set]',
      municipalityNum: -1,
      municipalityName: '[not set]',
      precipitation: -1,
      temperature: -1,
      sunshine: -1,
      searchToggle: -1
    }
    this.onPostcodeSelection = this.onPostcodeSelection.bind(this)
  }

  onPostcodeSelection(selection) {
    d3.json(`./postcodes/${selection.PLZ6}.json`).then(data => {
      this.setState({
        searchToggle: this.state.searchToggle * -1,
        userIsSelecting: false,
        locationNum: selection.GDENR,
        locationName: selection.ORTNAME,
        precipitation: 1,
        temperature: 1,
        sunshine: 1,
        ...data
      })
    })
  }

  render() {
    console.log(this.state)

    return (
      <div className='index'>
        <Masthead articleId={ARTICLE_ID} inverted />
        <ThemeSwitch />

        <header>
          <Cover
            title='So war der Sommer an Ihrem Wohnort'
            subtitle='Dauerregen, Unwetter, wenig Sonne: Die Schweiz erlebte mässige Sommermonate – aber nicht überall spielte das Wetter gleich verrückt. Finden Sie heraus, wie stark Ihre Region davon betroffen war.'
          />
        </header>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='ta metainfo'>
              <div className='ta author'>
                Yannick Wiget, Andreas Moor, Mathias Lutz, Marc Brupbacher
              </div>
              <div className='ta updated'>Aktualisiert am 26. August 2021</div>
            </div>
            <div className='ta text'>
              <p>
                Im Juni zerstörten schwere Hagelgewitter Autos, Hausdächer und
                Ernten im Berner Oberland und in der Zentralschweiz. Im Juli
                hinterliess ein verheerendes Unwetter über Zürich eine Schneise
                der Verwüstung. Hochwasser führte vielerorts zu
                Überschwemmungen. Und bis in den August hinein gab es verbreitet
                Starkregen. Solche Extremereignisse prägen die kollektive
                Erinnerung. Die meisten Menschen in der Schweiz dürften den
                Sommer deshalb kühl und nass erlebt haben.
              </p>
              <p>
                Aber war das wirklich so? Unsere Auswertung von Daten von Meteo
                Schweiz zeigt, dass die gefühlte und die tatsächliche Realität
                auseinanderliegen können: Im Vergleich zum langjährigen Mittel
                (1981 bis 2010) war dieser Sommer zu warm (Juni: +2.5 Grad;
                Juli: –0.3 Grad; August: noch ausstehend, bis jetzt aber wärmer
                als im Mittel). Tatsächlich regnete es aber deutlich mehr als
                üblich zu dieser Jahreszeit, und auch die Sonne zeigte sich
                seltener.
              </p>
              <p>
                Regional gibt es jedoch starke Unterschiede. Sie können Ihren
                Wohnort oder Ihre Postleitzahl eingeben und überprüfen, wie
                aussergewöhnlich dieser Sommer wirklich war. Angezeigt wird die
                Abweichung vom langjährigen Mittel bei Temperatur, Sonnenschein
                und Niederschlag:
              </p>
              <h4>Geben Sie Ihren Wohnort ein:</h4>
            </div>
          </div>
          {this.state.searchToggle < 0 && (
            <div className='ui text search container'>
              <MunicipalitySearch
                dedupe={false}
                onSelectionHandler={this.onPostcodeSelection}
              />
            </div>
          )}
          {/* two search forms as workaround to set back the input form */
          this.state.searchToggle > 0 && (
            <div className='ui text search container'>
              <MunicipalitySearch
                dedupe={false}
                onSelectionHandler={this.onPostcodeSelection}
              />
            </div>
          )}
          {this.state.locationNum > 0 && (
            <div className='ui stats'>
              <div className='box'>
                <div className='inshort'>
                  In <strong>{this.state.locationName}</strong> gab es vom{' '}
                  <strong>
                    <nobr>1. Juni</nobr> bis <nobr>19. August</nobr> 2021
                  </strong>{' '}
                  {this.state.snshn_diff > 0.5
                    ? 'überdurchschnittlich'
                    : this.state.snshn_diff < -0.5
                    ? 'unterdurchschnittlich'
                    : 'durchschnittlich'}{' '}
                  viel Sonnenschein. Die Temperatur lag{' '}
                  {this.state.temp_diff > 0.2
                    ? 'oberhalb'
                    : this.state.temp_diff < -0.2
                    ? 'unterhalb'
                    : 'im Bereich'}{' '}
                  des langjährigen Mittelwertes (1991–2020):
                </div>

                <div className='ui horizontal divided list'>
                  <div className='item'>
                    <div className='criterion'>Sonnenscheindauer</div>
                    <img
                      className='ui avatar image'
                      src={require('../../images/sun.svg')}
                    />
                    <div className='content'>
                      <div className='header'>
                        {this.state.snshn_diff >= 0 ? '+' : ''}
                        {numberFormat(this.state.snshn_diff)}%
                      </div>
                      <div className='average'>
                        Norm: {numberFormat(this.state.snshn_hist)}% des
                        <br />
                        möglichen Maximums
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: '2em' }}>
                  <div className='ui horizontal divided list'>
                    <div className='item'>
                      <div className='criterion'>Niederschlag</div>
                      <img
                        className='ui avatar image'
                        src={require('../../images/precipitation.svg')}
                      />
                      <div className='content'>
                        <div className='header'>
                          {numberFormat(this.state.precip_diff)}%
                        </div>
                        <div className='average'>
                          Norm: {numberFormat(this.state.precip_hist)}mm
                          <br />
                          (100%)
                        </div>
                      </div>
                    </div>
                    <div className='item'>
                      <div className='criterion'>Temperatur</div>

                      <img
                        className='ui avatar image'
                        src={require('../../images/temperature.svg')}
                      />
                      <div className='content'>
                        <div className='header'>
                          {this.state.temp_diff >= 0 ? '+' : ''}
                          {numberFormat(this.state.temp_diff)} °C
                        </div>
                        <div className='average'>
                          Norm: {numberFormat(this.state.temp_hist)} °C
                          <br />
                          &nbsp;
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {this.state.locationNum > 0 && (
            <div className='ui container graphic'>
              <h4>
                So waren die Sonnenstunden in {this.state.locationName} verteilt
              </h4>
              <div className='hint'>
                Maximal mögliche Sonnenscheindauer an diesem Tag
              </div>
              <img
                className='annotation'
                src={require('../../images/annotation.svg')}
              />
              <SunGrid data={this.state.sunshine} />
            </div>
          )}
        </div>
        <div className='ui vertical grey segment'>
          <div className='ui text container'>
            <div className='ta text'>
              <p>
                Besonders gross ist die Abweichung beim Niederschlag. Vielerorts
                fiel extrem viel mehr Regen als normalerweise im Sommer. Etliche
                Messstandorte in der Nord- und Zentralschweiz verzeichneten im
                Juli noch nie solche Niederschlagsmengen. Im landesweiten Mittel
                war es der nasseste Juli seit Messbeginn 1864.
              </p>
              <p>
                Über den gesamten Beobachtungszeitraum gesehen beklagte
                Spitzenreiter Bösingen im Kanton Freiburg doppelt so viel Regen
                wie normal. Nicht alle erlebten aber einen überdurchschnittlich
                nassen Sommer. So regnete es in Klosters genau so viel wie im
                Schnitt von 1990 bis 2020.
              </p>
            </div>
          </div>

          <div className='ui container graphic'>
            <h4>Im ganzen Land viel Niederschlag</h4>
            <div className='description'>
              Niederschlagssumme 1. Juni bis 19. August, Prozent der Norm
              (1991–2020)
            </div>
            <div className='color-scale'>
              <div className='step' style={{ backgroundColor: '#f2f5f2' }}>
                <div className='label'>100%</div>
              </div>
              <div className='step' style={{ backgroundColor: '#b6dddb' }}>
                <div className='label'>&nbsp;</div>
              </div>
              <div className='step' style={{ backgroundColor: '#75c2c0' }}>
                <div className='label'>140%</div>
              </div>
              <div className='step' style={{ backgroundColor: '#35a6a3' }}>
                <div className='label'>&nbsp;</div>
              </div>
              <div className='step' style={{ backgroundColor: '#207674' }}>
                <div className='label'>180%</div>
              </div>
            </div>
            <div>
              <Highlighter
                baseImage={
                  isMobile
                    ? require('../../images/niederschlag-mobile.png')
                    : require('../../images/niederschlag.png')
                }
                overlayImage={
                  isMobile
                    ? require('../../images/niederschlag-mobile-overlay.png')
                    : require('../../images/niederschlag-overlay.png')
                }
              />
            </div>
          </div>

          <div className='ui text container'>
            <div className='ta text'>
              <p>
                Niederschlag und Sonnenscheindauer hängen natürlich miteinander
                zusammen. In diesem grösstenteils verregneten Sommer schien die
                Sonne fast überall in der Schweiz seltener als sonst im Schnitt.
              </p>
              <p>
                Einen mehrheitlich bewölkten Sommer und die grösste Abweichung
                der Norm erlebte Novalles in der Waadt. Hier zeigte sich die
                Sonne rund 10 Prozent seltener als normal in der beobachteten
                Periode. Orte, wo die Sonne etwas häufiger als im Durchschnitt
                der letzten 30 Jahre schien, gab es nur vereinzelt in den
                Kantonen Wallis, Graubünden und im Tessin.
              </p>
            </div>
          </div>

          <div className='ui container graphic'>
            <h4>Sonnenschein weit unter dem Potenzial</h4>
            <div className='description'>
              Sonnenscheindauer 1. Juni bis 19. August, Abweichung der Norm
              (1991–2020) in Prozent der maximal möglichen Dauer
            </div>
            <div className='color-scale'>
              <div className='step' style={{ backgroundColor: '#555555' }}>
                <div className='label'>-10%</div>
              </div>
              <div className='step' style={{ backgroundColor: '#a5a5a5' }}>
                <div className='label'>&nbsp;</div>
              </div>
              <div className='step' style={{ backgroundColor: '#dcdcdc' }}>
                <div className='label'>-5%</div>
              </div>
              <div className='step' style={{ backgroundColor: '#f0f0f0' }}>
                <div className='label'>&nbsp;</div>
              </div>
              <div className='step' style={{ backgroundColor: '#f9f6e0' }}>
                <div className='label'>0%</div>
              </div>
              <div className='step' style={{ backgroundColor: '#fef5c0' }}>
                <div className='label'>&nbsp;</div>
              </div>
              <div className='step' style={{ backgroundColor: '#fce98a' }}>
                <div className='label'>+5%</div>
              </div>
              <div className='step' style={{ backgroundColor: '#fcd548' }}>
                <div className='label'>&nbsp;</div>
              </div>
              <div className='step' style={{ backgroundColor: '#fdbb01' }}>
                <div className='label'>+10%</div>
              </div>
            </div>
            <div>
              <Highlighter
                baseImage={
                  isMobile
                    ? require('../../images/sonnenschein-mobile.png')
                    : require('../../images/sonnenschein.png')
                }
                overlayImage={
                  isMobile
                    ? require('../../images/sonnenschein-mobile-overlay.png')
                    : require('../../images/sonnenschein-overlay.png')
                }
              />
            </div>
          </div>

          <div className='ui text container'>
            <div className='ta text'>
              <p>
                Trotzdem war es wärmer als im langjährigen Mittel – und dies,
                obwohl wir für unsere Berechnungen die neuste Referenzperiode
                1991–2020 verwendet haben, seit der sich der Klimawandel immer
                stärker bemerkbar macht. Der Juni war schweizweit sogar der
                viertwärmste seit Messbeginn. Dafür war es im Juli etwas zu
                kalt. Der August ist noch nicht abgeschlossen, dürfte aber auch
                wärmer gegenüber der Norm sein.
              </p>
            </div>
          </div>
          <div className='ui container graphic'>
            <h4>Im Südosten leicht überdurchschnittliche Temperaturen</h4>
            <div className='description'>
              Mittlere Temperatur 1. Juni bis 19. August, Abweichung von der
              Norm (1991–2020), Grad Celsius
            </div>
            <div className='color-scale'>
              <div className='step' style={{ backgroundColor: '#296a8d' }}>
                <div className='label'>-0.8°</div>
              </div>
              <div className='step' style={{ backgroundColor: '#599abd' }}>
                <div className='label'>&nbsp;</div>
              </div>
              <div className='step' style={{ backgroundColor: '#9cc6de' }}>
                <div className='label'>-0.4°</div>
              </div>
              <div className='step' style={{ backgroundColor: '#d6e5ea' }}>
                <div className='label'>&nbsp;</div>
              </div>
              <div className='step' style={{ backgroundColor: '#efece0' }}>
                <div className='label'>0.0°</div>
              </div>
              <div className='step' style={{ backgroundColor: '#f3d4d3' }}>
                <div className='label'>&nbsp;</div>
              </div>
              <div className='step' style={{ backgroundColor: '#e9979d' }}>
                <div className='label'>+0.4°</div>
              </div>
              <div className='step' style={{ backgroundColor: '#cd555e' }}>
                <div className='label'>&nbsp;</div>
              </div>
              <div className='step' style={{ backgroundColor: '#9e272f' }}>
                <div className='label'>+0.8°</div>
              </div>
            </div>
            <div>
              <Highlighter
                baseImage={
                  isMobile
                    ? require('../../images/temperatur-mobile.png')
                    : require('../../images/temperatur.png')
                }
                overlayImage={
                  isMobile
                    ? require('../../images/temperatur-mobile-overlay.png')
                    : require('../../images/temperatur-overlay.png')
                }
              />
            </div>
          </div>
          {/* <div className='ui container graphic'>
            <h4>Am Genfersee fielen die meisten Sommertage aus</h4>
            <div className='description'>
              Anzahl Sommertage (d.h. wärmer als 25°) zwischen 1. Juni bis 11.
              August, Abweichung von der Norm (1991–2020)
            </div>
            <div>
              <img
                className='ui fluid image'
                src={require('../../images/summer_days.png')}
              />
            </div>
          </div> */}
        </div>

        <Footer
          hidePortfolioTeaser
          articleId={ARTICLE_ID}
          credits={[
            ['Text', 'Yannick Wiget und Marc Brupbacher'],
            ['Umsetzung', 'Andreas Moor und Mathias Lutz']
          ]}
        >
          <div
            className='ui text container methodik'
            style={{ position: 'relative', paddingTop: '1em' }}
          >
            <h5 style={{ fontSize: '1.4em' }}>Methodik</h5>

            <p>
              MeteoSchweiz bietet Datensätze der{' '}
              <Link
                allowWordWrap
                href='https://www.meteoschweiz.admin.ch/content/dam/meteoswiss/de/service-und-publikationen/produkt/raeumliche-daten-sonnenschein/doc/ProdDoc_SrelD.pdf'
              >
                tägliche relativen Sonnenscheindauer
              </Link>
              ,{' '}
              <Link
                allowWordWrap
                href='https://www.meteoschweiz.admin.ch/content/dam/meteoswiss/de/service-und-publikationen/produkt/raeumliche-daten-temperatur/doc/ProdDoc_TabsD.pdf'
              >
                täglichen Temperatur
              </Link>{' '}
              sowie{' '}
              <Link
                allowWordWrap
                href='https://www.meteoschweiz.admin.ch/content/dam/meteoswiss/de/service-und-publikationen/produkt/raeumliche-daten-niederschlag/doc/ProdDoc_RhiresD.pdf'
              >
                täglichen Niederschläge
              </Link>{' '}
              in einer räumlichen Auflösung von einem Quadratkilometer an. Für
              diese Sommer-Bilanz wurden die Elemente dieses Rasters auf die
              Schweizer PLZ-Gebiete aufgeteilt und anschliessend die Mittelwerte
              der Perioden 1. Juni bis 19. August seit 1991 mit den diesjährigen
              verglichen.
            </p>
            <p>
              Die Sonnenscheindauer gibt MeteoSchweiz in Prozent des maximal
              möglichen Zeitraums an – einem ganztags wolkenlosen Himmel. Eine
              Abweichung von –5,5% in Zürich bedeutet beispielsweise, dass
              diesen Sommer nur 45,4% des Maximums erreicht wurden anstatt der
              50,9% im langjährigen Mittel.
            </p>
          </div>
        </Footer>
      </div>
    )
  }
}
